import { filter } from 'rxjs';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { SidebarComponent, SidebarModule } from '@syncfusion/ej2-angular-navigations';
import { ModalDialog, ModalService } from './base/shared/modal/modal.service';
import { PreventShrinkService } from './base/services/prevent-shrink.service';
import { LanguageService } from './base/services/language.service';
import { WideFormatService } from './base/services/wide-format.service';
import { Component, OnInit, HostListener, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { AuthService } from './base/services/auth.service';
import { environment } from 'src/environments/environment';
import { homeRoute } from './core/routing/route-names';
import { ModalComponent } from './base/shared/modal/modal.component';
import { ToastsComponent } from './base/toasts/toasts.component';
import { SpinnerComponent } from './base/spinner/spinner.component';
import { NavigationComponent } from './base/navigation/navigation.component';
import { NgClass } from '@angular/common';
import { confirmEmailPageName } from './core/routing/page-names';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [SidebarModule, NgClass, NavigationComponent, SpinnerComponent, RouterOutlet, ToastsComponent, ModalComponent]
})
export class AppComponent implements OnInit {
  @ViewChild(SidebarComponent) sideBar: SidebarComponent;

  @HostListener('document:click', ['$event'])
  sideBarClickTrack(event) {
    if(this.sideBar?.element.contains(event.target)) {
      if (!this.sideBar.isOpen) {
        this.sideBar.show();
        this.isOpen = true;
      }
    } else {
      if (this.sideBar.isOpen) {
        // we do not want to close sidebar on home route
        const subroutes: string[] = [homeRoute + '/'];
        const url = (this.router.url + '/').toLocaleLowerCase();
        var subRouteMatch = subroutes.find(s => url.indexOf(s) > -1);
        if (!subRouteMatch && !this.isPinned) {
          this.sideBar.hide();
          this.isOpen = false;
        }
      }
    }
  }

  public get useWideFormat() {
    return this.wideFormatService.useWideFormat;
  }

  public get noShrink() {
    return this.preventShrinkService.preventShrink;
  }

  public get isSidebarOpen(): boolean {
    return this.isOpen;
  }

  public isPinned = false;
  public version = '';

  private isOpen = true;
  private timeoutWarning: ModalDialog;

  private hiddenSidebarRoutes: string[] = [
    `/${confirmEmailPageName}`
  ];

  constructor(private translate: TranslateService,
              private keepAlive: Keepalive,
              private idle: Idle,
              private modalService: ModalService,
              private authService: AuthService,
              private languageService: LanguageService,
              private wideFormatService: WideFormatService,
              private preventShrinkService: PreventShrinkService,
              private router: Router) {
    // Initialize the languages
    this.languageService.init();

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    // const browserLang: string = translate.getBrowserLang();
    // translate.use(browserLang.match(/de/) ? browserLang : 'hu');
    this.configureIdle();
    this.configureKeepAlive();
    this.version = 'V' + environment.version;
    this.isPinned = localStorage[environment.localStorageIsSidebarPinned] ? localStorage[environment.localStorageIsSidebarPinned] === "true" : true;
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      const url = e.url.toLocaleLowerCase();
      const match = this.hiddenSidebarRoutes.find(route => url.indexOf(route) > -1);
      if (match != null) {
        setTimeout(() => {
          this.sideBar.hide();
          this.isOpen = false;
        }, 0);
      }
    });
  }

  public ngOnInit(): void {
    this.addEnvInfoClassToBody();
    // hide sidebar on mobile
    if (window.innerWidth < 800) {
      this.isOpen = false;
      this.isPinned = false;
    }
  }

  public onPinToggle() {
    this.isPinned = !this.isPinned;
    localStorage[environment.localStorageIsSidebarPinned] = this.isPinned;
  }

  private configureKeepAlive() {
    this.keepAlive.interval(30);
    this.keepAlive.onPing.subscribe(() => {
      this.authService.updateActivityTimeStamp();
    });
  }

  private configureIdle() {
    this.idle.setIdle(environment.idleTimeoutInSeconds);
    this.idle.setTimeout(60);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe(() => {
      const dialog = this.timeoutWarning;
      this.modalService.closeDialog(dialog);
      this.timeoutWarning = null;
    });

    this.idle.onTimeout.subscribe(() => {
      this.modalService.closeDialog(this.timeoutWarning);
      this.timeoutWarning = null;
      this.authService.logout();
    });

    this.idle.onTimeoutWarning.subscribe((countdown) => {
      const message = this.translate.instant('i18n.core.modal.idle.message', { params: countdown });
      if (!this.timeoutWarning) {
        if (!this.idle.isIdling()) {
          return;
        }
        this.timeoutWarning = this.modalService.showMessageDialog('i18n.core.modal.idle.title', message);
      } else {
        this.timeoutWarning.message = this.translate.instant('i18n.core.modal.idle.message', { params: countdown });
      }
    });
  }

  private addEnvInfoClassToBody() {
    const bodyTag = document.body;
    if (environment.envInfoClass && environment.envInfoClass.length > 0) {
      bodyTag.classList.add(environment.envInfoClass);
    }
  }
}
